import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import {
  MdCheckCircle, MdCancel, MdAccessTime, MdGroups, MdEventAvailable,
  MdFilterList, MdSave, MdGridOn, MdViewList, MdFileDownload, MdEditNote, MdFactCheck, MdHistory,
} from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import { useNavIntent } from '../navIntent';
import PageShell, { Button } from '../../ui/PageShell';
import Card, { EmptyState } from '../../ui/Card';
import { Field, Select, Input } from '../../ui/Form';
import Table from '../../ui/Table';
import { sectionLabel } from '../../ui/labels';
import AttendanceActionModal from './AttendanceActionModal';
import './attendance.css';

const STATUS_AR = { present: 'حاضر', absent: 'غائب', late: 'متأخر' };
/** "2026-09-24 18:21" in school time (Syria, UTC+3). */
const fmtWhen = (iso) => (iso ? new Date(new Date(iso).getTime() + 3 * 3600e3).toISOString().slice(0, 16).replace('T', ' ') : '—');

/** Who took a register and when; flags a back-dated one and later edits. */
function TakenInfo({ day }) {
  if (!day) return null;
  return (
    <span className="att-taken-info">
      <span>سجّله {day.marked_by_name || '—'} · <bdi dir="ltr">{fmtWhen(day.taken_at || day.created_at)}</bdi></span>
      {day.taken_late ? <span className="att-flag is-warn" title="سُجّل هذا التفقّد في يوم لاحق لتاريخه">سُجّل بتاريخ لاحق</span> : null}
      {day.late_changes_count > 0 && (
        <span className="att-flag is-danger" title="تعديلات أُجريت في يوم لاحق لتاريخ التفقّد">
          <MdHistory size={13} /> {day.late_changes_count} تعديل لاحق
        </span>
      )}
      {day.changes_count > day.late_changes_count && (
        <span className="att-flag" title="تعديلات في نفس اليوم">{day.changes_count - day.late_changes_count} تعديل بنفس اليوم</span>
      )}
    </span>
  );
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/** The school week runs Sunday–Thursday; the API rejects Fri/Sat (unless
 *  the section's grade comes on that day), so the screen says so up front
 *  instead of failing on submit. */
const isWeekend = (d, section) => {
  const day = new Date(`${d}T00:00:00`).getDay();
  return (day === 5 && !section?.works_friday) || (day === 6 && !section?.works_saturday);
};

const ARABIC_MONTHS = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
  'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];
const ARABIC_WEEKDAYS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
/** "الأحد 21 أيل" — the weekday name is what makes a date scannable in
 *  a list; "21 أيل" alone forces the reader to compute the day. */
const shortDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${ARABIC_WEEKDAYS[d.getDay()]} ${d.getDate()} ${ARABIC_MONTHS[d.getMonth()].substring(0, 3)}`;
};
const dayMonth = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUSES = [
  { v: 'present', label: 'حاضر', Icon: MdCheckCircle },
  { v: 'late', label: 'متأخر', Icon: MdAccessTime },
  { v: 'absent', label: 'غائب', Icon: MdCancel },
];

/**
 * Attendance console. Three things live here, matching the old page:
 *   1. Statistics for the filtered range (totals, overall rate, last 7 days)
 *   2. Taking today's register for a section (the entry form)
 *   3. Reviewing past registers — as a list or as a month grid
 *
 * Storage is absence-only (a present pupil has no mark row), so the
 * register starts everyone present and only the exceptions are sent.
 */
export default function Attendance() {
  const toast = useToastWithSound();
  const { data: sections, loading: loadingSections } = useData(() => api.sections(), []);

  const [sectionId, setSectionId] = useState('');
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(todayStr());
  const [registerDate, setRegisterDate] = useState(todayStr());
  // 'records' is the default because reviewing/correcting what was
  // recorded is what this page is actually for.
  const [view, setView] = useState('records'); // 'records' | 'list' | 'grid'
  const [tab, setTab] = useState('register'); // 'register' | 'history'

  const [roster, setRoster] = useState(null);
  const [marks, setMarks] = useState({});
  const [notes, setNotes] = useState({});
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Correcting one pupil's mark after the fact: { record, action }
  const [correction, setCorrection] = useState(null);
  const [correcting, setCorrecting] = useState(false);

  const activeSections = (sections || []).filter((s) => s.status === 'active');

  // Navbar search → a section's «التفقّد»: open its register.
  useNavIntent('attendance', (it) => {
    if (it.sectionId) setSectionId(String(it.sectionId));
  });

  const loadRoster = useCallback(async () => {
    if (!sectionId) { setRoster(null); return; }
    setLoadingRoster(true);
    try {
      const r = await api.sectionRoster(sectionId, registerDate);
      setRoster(r);
      const nextMarks = {};
      const nextNotes = {};
      r.students.forEach((s) => {
        nextMarks[s.student_id] = s.status;
        if (s.note) nextNotes[s.student_id] = s.note;
      });
      setMarks(nextMarks);
      setNotes(nextNotes);
    } catch (err) {
      toast.error(err.message);
      setRoster(null);
    } finally { setLoadingRoster(false); }
  }, [sectionId, registerDate]);

  const loadHistory = useCallback(async () => {
    if (!sectionId) { setHistory(null); return; }
    setLoadingHistory(true);
    try {
      setHistory(await api.sectionAttendanceHistory(sectionId, { start_date: startDate, end_date: endDate }));
    } catch (err) {
      toast.error(err.message);
      setHistory(null);
    } finally { setLoadingHistory(false); }
  }, [sectionId, startDate, endDate]);

  useEffect(() => { loadRoster(); }, [loadRoster]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setMark = (studentId, status) => {
    setMarks((m) => ({ ...m, [studentId]: status }));
    if (status === 'present') {
      setNotes((n) => { const next = { ...n }; delete next[studentId]; return next; });
    }
  };
  const setNote = (studentId, text) => setNotes((n) => ({ ...n, [studentId]: text }));
  const setAll = (status) => {
    const next = {};
    roster.students.forEach((s) => { next[s.student_id] = status; });
    setMarks(next);
    if (status === 'present') setNotes({});
  };

  const liveCounts = Object.values(marks).reduce(
    (acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; },
    { present: 0, absent: 0, late: 0 }
  );

  /** Stats over the filtered history range — the three-layer block the
   *  old page had: totals, overall rate, and a last-7-days breakdown. */
  const stats = useMemo(() => {
    const records = history?.records || [];
    const total = records.length;
    const present = records.filter((r) => r.status === 'present').length;
    const absent = records.filter((r) => r.status === 'absent').length;
    const late = records.filter((r) => r.status === 'late').length;
    const pct = (n) => (total > 0 ? ((n / total) * 100).toFixed(1) : '0.0');

    const byDate = records.reduce((acc, r) => {
      if (!acc[r.date]) acc[r.date] = { present: 0, absent: 0, late: 0, total: 0 };
      acc[r.date].total += 1;
      acc[r.date][r.status] += 1;
      return acc;
    }, {});

    // "This week" = the school week (Sunday–Thursday) containing today,
    // not just "whatever 7 dates were last recorded" — so the card
    // always answers "how is this week going", and a day with no
    // register yet still shows up (as "لم يُسجَّل") instead of vanishing.
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    const weekDates = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      return d.toISOString().slice(0, 10);
    });

    const daily = weekDates
      .filter((date) => date <= todayStr())
      .sort((a, b) => new Date(b) - new Date(a))
      .map((date) => {
        const day = byDate[date];
        return {
          date,
          present: day?.present || 0,
          absent: day?.absent || 0,
          late: day?.late || 0,
          total: day?.total || 0,
          rate: day && day.total > 0 ? (((day.present + day.late) / day.total) * 100).toFixed(1) : null,
        };
      });

    return {
      total, present, absent, late,
      presentPct: pct(present), absentPct: pct(absent), latePct: pct(late),
      rate: total > 0 ? (((present + late) / total) * 100).toFixed(1) : '0.0',
      daily,
    };
  }, [history]);

  /** Month-grid shape: students as rows, dates as columns. */
  const grid = useMemo(() => {
    const records = history?.records || [];
    if (records.length === 0) return null;
    const students = new Map();
    const dates = new Set();
    const cells = new Map();
    records.forEach((r) => {
      if (!students.has(r.student_id)) students.set(r.student_id, r.student_name);
      dates.add(r.date);
      cells.set(`${r.student_id}|${r.date}`, r);
    });
    const sortedDates = [...dates].sort();
    const rows = [...students.entries()].map(([id, name]) => {
      const perDay = sortedDates.map((d) => cells.get(`${id}|${d}`) || null);
      const attended = perDay.filter((c) => c && (c.status === 'present' || c.status === 'late')).length;
      const absentDays = perDay.filter((c) => c && c.status === 'absent').length;
      const counted = perDay.filter(Boolean).length;
      return {
        id, name, perDay, attended, absentDays,
        rate: counted > 0 ? ((attended / counted) * 100).toFixed(1) : '0.0',
      };
    });
    return { dates: sortedDates, rows };
  }, [history]);

  const save = async () => {
    setSaving(true);
    try {
      const ids = (status) => Object.entries(marks)
        .filter(([, s]) => s === status)
        .map(([id]) => Number(id));
      const absentIds = ids('absent');
      const lateIds = ids('late');

      await api.takeAttendance({
        section_id: Number(sectionId),
        date: registerDate,
        absent_student_ids: absentIds,
        late_student_ids: lateIds,
      });

      // Notes can only attach to a mark row, which only absent/late
      // pupils have — sent as a follow-up PATCH per noted student.
      const noted = [...absentIds, ...lateIds].filter((id) => notes[id]?.trim());
      if (noted.length > 0) {
        const results = await Promise.allSettled(
          noted.map((id) => api.updateAttendanceMark(sectionId, registerDate, id, { note: notes[id].trim() }))
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) toast.warning(`تم حفظ التفقّد، لكن فشل حفظ ${failed} ملاحظة`);
      }

      toast.success('تم حفظ التفقّد بنجاح');
      loadRoster();
      loadHistory();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  /** Apply a post-hoc correction to one pupil's mark for one day. */
  const applyCorrection = async (status, note) => {
    const { record } = correction;
    setCorrecting(true);
    try {
      await api.updateAttendanceMark(sectionId, record.date, record.student_id, { status, note });
      toast.success('تم تحديث الحضور بنجاح');
      setCorrection(null);
      loadHistory();
      // The register tab may be showing the same day — keep it in step.
      if (record.date === registerDate) loadRoster();
    } catch (err) {
      toast.error(err.message);
    } finally { setCorrecting(false); }
  };

  const exportGrid = () => {
    if (!grid) return;
    const rows = [
      ['مؤسسة المتميزون التعليمية'],
      ['سجل الحضور والغياب'],
      [],
      [`من تاريخ: ${startDate} إلى: ${endDate}`],
      [],
      ['اسم الطالب', ...grid.dates.map(dayMonth), 'عدد الحضور', 'عدد الغيابات', 'نسبة الحضور'],
      ...grid.rows.map((r) => [
        r.name,
        ...r.perDay.map((c) => (!c ? '-' : c.status === 'present' ? '✓' : c.status === 'late' ? '⏰' : '✗')),
        r.attended, r.absentDays, `${r.rate}%`,
      ]),
    ];
    const csv = '﻿' + rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `attendance_${startDate}_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('تم تصدير السجل');
  };

  const sectionPicked = !!sectionId;

  return (
    <PageShell title="التفقّد" description="تسجيل الحضور والغياب ومراجعة السجل">
      {/* ---- Filters ---------------------------------------------- */}
      <Card>
        <div className="att-filters">
          <Field label="الشعبة" required>
            <Select
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              placeholder={loadingSections ? 'جارٍ التحميل...' : 'اختر الشعبة'}
              options={activeSections.map((s) => ({ value: s.id, label: `${sectionLabel(s)} — ${s.student_count ?? 0} طالب` }))}
            />
          </Field>
          <Field label="من تاريخ">
            <Input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="إلى تاريخ">
            <Input type="date" value={endDate} max={todayStr()} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
      </Card>

      {!sectionPicked && (
        <Card>
          <EmptyState icon={<MdFilterList size={40} />} title="اختر شعبة" message="اختر الشعبة والفترة لعرض السجل وأخذ التفقّد." />
        </Card>
      )}

      {sectionPicked && (
        <>
          {/* ---- Statistics ---------------------------------------- */}
          <div className="att-stats">
            <div className="att-stat-total">
              <span className="att-stat-total-label">إجمالي السجلات</span>
              <span className="att-stat-total-value">{stats.total}</span>
              <span className="att-stat-total-hint">خلال الفترة المحددة</span>
            </div>

            <div className="att-stat-combined">
              {[
                { key: 'present', label: 'حاضر', value: stats.present, pct: stats.presentPct, Icon: MdCheckCircle },
                { key: 'absent', label: 'غائب', value: stats.absent, pct: stats.absentPct, Icon: MdCancel },
                { key: 'late', label: 'متأخر', value: stats.late, pct: stats.latePct, Icon: MdAccessTime },
              ].map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && <div className="att-stat-divider" />}
                  <div className={`att-stat-part is-${s.key}`}>
                    <span className="att-stat-part-icon"><s.Icon size={22} /></span>
                    <div>
                      <span className="att-stat-part-label">{s.label}</span>
                      <span className="att-stat-part-value">{s.value} <small>({s.pct}%)</small></span>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="att-substats">
            <div className="att-subcard">
              <h4 className="att-subcard-title">معدل الحضور الإجمالي</h4>
              <div className="att-rate">
                <div className="att-rate-value">{stats.rate}%</div>
                <div className="att-rate-bar"><div className="att-rate-fill" style={{ width: `${stats.rate}%` }} /></div>
                <div className="att-rate-hint">من إجمالي {stats.total} سجل</div>
              </div>
            </div>

            <div className="att-subcard">
              <h4 className="att-subcard-title">ملخص هذا الأسبوع</h4>
              <div className="att-daily">
                {stats.daily.length === 0 ? (
                  <div className="att-daily-empty">لا توجد بيانات</div>
                ) : stats.daily.map((d) => (
                  <div key={d.date} className={`att-daily-row${d.rate === null ? ' is-unrecorded' : ''}`}>
                    <span className="att-daily-date">{shortDate(d.date)}</span>
                    {d.rate === null ? (
                      <span className="att-daily-none">لم يُسجَّل بعد</span>
                    ) : (
                      <>
                        <span className="att-daily-counts">
                          <b className="is-present">{d.present}</b>
                          <i>/</i>
                          <b className="is-absent">{d.absent}</b>
                          <i>/</i>
                          <b className="is-late">{d.late}</b>
                        </span>
                        <span className="att-daily-rate">{d.rate}%</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---- Tabs: take register / review history --------------- */}
          <div className="att-tabs">
            <button type="button" className={`att-tab${tab === 'register' ? ' is-active' : ''}`} onClick={() => setTab('register')}>
              <MdEditNote size={18} /> أخذ التفقّد
            </button>
            <button type="button" className={`att-tab${tab === 'history' ? ' is-active' : ''}`} onClick={() => setTab('history')}>
              <MdEventAvailable size={18} /> السجل
            </button>
          </div>

          {tab === 'register' && (
            <RegisterPanel
              section={(sections || []).find((s) => String(s.id) === String(sectionId))}
              registerDate={registerDate}
              setRegisterDate={setRegisterDate}
              roster={roster}
              loading={loadingRoster}
              marks={marks}
              notes={notes}
              setMark={setMark}
              setNote={setNote}
              setAll={setAll}
              counts={liveCounts}
              saving={saving}
              onSave={save}
            />
          )}

          {tab === 'history' && (
            <Card
              title={`سجل التفقّد (${history?.days?.length || 0} يوم)`}
              actions={
                <div className="att-view-actions">
                  <div className="att-view-toggle">
                    <button type="button" className={view === 'records' ? 'is-active' : ''} onClick={() => setView('records')} title="سجلات الطلاب">
                      <MdFactCheck size={18} />
                    </button>
                    <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')} title="ملخص الأيام">
                      <MdViewList size={18} />
                    </button>
                    <button type="button" className={view === 'grid' ? 'is-active' : ''} onClick={() => setView('grid')} title="عرض شبكة">
                      <MdGridOn size={18} />
                    </button>
                    <button type="button" className={view === 'changes' ? 'is-active' : ''} onClick={() => setView('changes')} title="سجل التعديلات">
                      <MdHistory size={18} />
                    </button>
                  </div>
                  {view === 'grid' && grid && (
                    <Button variant="secondary" onClick={exportGrid}><MdFileDownload size={16} /> تصدير</Button>
                  )}
                </div>
              }
              padded={false}
            >
              {loadingHistory ? (
                <div className="att-loading">جارٍ التحميل...</div>
              ) : !history || history.days.length === 0 ? (
                <EmptyState icon={<MdEventAvailable size={40} />} title="لا توجد سجلات" message="لم يُسجَّل تفقّد لهذه الشعبة خلال الفترة المحددة." />
              ) : view === 'changes' ? (
                <ChangesView sectionId={sectionId} startDate={startDate} endDate={endDate} reloadKey={history} />
              ) : view === 'grid' ? (
                <GridView grid={grid} />
              ) : view === 'records' ? (
                <RecordsView records={history.records} onCorrect={(record, action) => setCorrection({ record, action })} />
              ) : (
                <ListView days={history.days} />
              )}
            </Card>
          )}

          {correction && (
            <AttendanceActionModal
              record={correction.record}
              action={correction.action}
              busy={correcting}
              onConfirm={applyCorrection}
              onCancel={() => setCorrection(null)}
            />
          )}
        </>
      )}
    </PageShell>
  );
}

/** The register entry form: bulk actions, live counts, per-student row. */
function RegisterPanel({ section, registerDate, setRegisterDate, roster, loading, marks, notes, setMark, setNote, setAll, counts, saving, onSave }) {
  const weekend = isWeekend(registerDate, section);
  const locked = roster?.taken || weekend;

  return (
    <>
      {weekend && (
        <div className="ui-notice ui-notice-warning">
          <div className="ui-notice-title">عطلة نهاية الأسبوع</div>
          <div className="ui-notice-body">لا يوجد دوام يومي الجمعة والسبت، ولا يمكن تسجيل تفقّد في هذا التاريخ.</div>
        </div>
      )}

      <Card
        title={
          <span className="att-register-title">
            تفقّد يوم <b>{registerDate}</b>
            {roster?.taken && <span className="att-chip is-done">مسجَّل مسبقاً</span>}
            {roster?.taken && <TakenInfo day={roster.roster} />}
          </span>
        }
        actions={
          <div className="att-register-actions">
            <Input type="date" value={registerDate} max={todayStr()} onChange={(e) => setRegisterDate(e.target.value)} />
            <Button variant="success" onClick={onSave} disabled={locked || !roster || roster.students.length === 0} loading={saving}>
              {!saving && <MdSave size={16} />} حفظ التفقّد
            </Button>
          </div>
        }
        padded={false}
      >
        {loading ? (
          <div className="att-loading">جارٍ التحميل...</div>
        ) : !roster || roster.students.length === 0 ? (
          <EmptyState icon={<MdGroups size={40} />} title="لا يوجد طلاب" message="لا يوجد طلاب مسجّلون في هذه الشعبة." />
        ) : (
          <>
            <div className="att-toolbar">
              <div className="att-bulk">
                <span className="att-bulk-label">تحديد الكل:</span>
                {STATUSES.map(({ v, label, Icon }) => (
                  <button key={v} type="button" className={`att-bulk-btn is-${v}`} onClick={() => setAll(v)} disabled={locked}>
                    <Icon size={16} /> {label}
                  </button>
                ))}
              </div>
              <div className="att-live">
                <span className="att-live-item is-present"><MdCheckCircle size={16} /> حاضر: <b>{counts.present || 0}</b></span>
                <span className="att-live-item is-absent"><MdCancel size={16} /> غائب: <b>{counts.absent || 0}</b></span>
                <span className="att-live-item is-late"><MdAccessTime size={16} /> متأخر: <b>{counts.late || 0}</b></span>
              </div>
            </div>

            <div className="att-grid">
              {roster.students.map((s, i) => {
                const status = marks[s.student_id];
                return (
                  <div key={s.student_id} className={`att-card is-${status}`}>
                    <div className="att-card-info">
                      <span className="att-card-index">{i + 1}</span>
                      <span className="att-card-name">{s.name}</span>
                    </div>

                    <div className="att-card-choices">
                      {STATUSES.map(({ v, label, Icon }) => (
                        <button
                          key={v}
                          type="button"
                          className={`att-icon-btn is-${v}${status === v ? ' is-active' : ''}`}
                          onClick={() => setMark(s.student_id, v)}
                          disabled={locked}
                          title={label}
                        >
                          <Icon size={20} />
                        </button>
                      ))}
                    </div>

                    {status !== 'present' ? (
                      <input
                        type="text"
                        className="att-card-note"
                        placeholder="ملاحظة (اختياري)"
                        value={notes[s.student_id] || ''}
                        onChange={(e) => setNote(s.student_id, e.target.value)}
                        disabled={locked}
                      />
                    ) : <span className="att-card-note-spacer" />}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </>
  );
}

/**
 * Every individual record in the range — one row per pupil per day —
 * with the three correction buttons the old page had. This is the
 * page's real purpose: reviewing and fixing what was recorded.
 */
function RecordsView({ records, onCorrect }) {
  const rows = records.map((r) => ({
    ...r,
    id: `${r.student_id}-${r.date}`,
    status_label: { present: 'حاضر', absent: 'غائب', late: 'متأخر' }[r.status],
  }));

  return (
    <Table
      keyField="id"
      rows={rows}
      emptyText="لا توجد سجلات"
      searchPlaceholder="ابحث بالاسم أو التاريخ أو الملاحظة..."
      pageSize={25}
      columns={[
        { key: 'date', label: 'التاريخ', nowrap: true, width: 1,
          render: (r) => <span className="att-date-chip">{r.date}</span> },
        { key: 'student_name', label: 'الطالب', width: 2, filterable: false,
          render: (r) => <strong>{r.student_name}</strong> },
        { key: 'status_label', label: 'الحالة', nowrap: true, align: 'center', width: 1,
          render: (r) => {
            const cfg = { present: [MdCheckCircle, 'present'], absent: [MdCancel, 'absent'], late: [MdAccessTime, 'late'] }[r.status];
            const Icon = cfg[0];
            return (
              <span className="att-status-cell">
                <span className={`att-status-badge is-${cfg[1]}`}><Icon size={15} /> {r.status_label}</span>
                {r.changes_count > 0 && (
                  <span
                    className={`att-flag${r.changed_late ? ' is-danger' : ''}`}
                    title={`كان ${STATUS_AR[r.last_change.old_status]} · عدّله ${r.last_change.changed_by_name || '—'} في ${fmtWhen(r.last_change.changed_at)}`}
                  >
                    <MdHistory size={13} /> {r.changed_late ? 'عُدّل لاحقاً' : 'عُدّل'}
                  </span>
                )}
              </span>
            );
          } },
        { key: 'note', label: 'ملاحظات', width: 2, filterable: false, clamp: 2,
          formatValue: (v) => v || '—' },
        { key: 'marked_by_name', label: 'سجّله', nowrap: true, width: 1,
          formatValue: (v) => v || '—' },
      ]}
      actionsLabel="تصحيح"
      renderActions={(r) => (
        <div className="att-quick-actions">
          {STATUSES.map(({ v, label, Icon }) => (
            <button
              key={v}
              type="button"
              className={`att-quick-btn is-${v}`}
              title={label}
              disabled={r.status === v}
              onClick={() => onCorrect(r, v)}
            >
              <Icon size={15} /><span>{label}</span>
            </button>
          ))}
        </div>
      )}
    />
  );
}

/** Past registers, one row per day taken. */
function ListView({ days }) {
  return (
    <div className="att-history">
      {days.map((d) => {
        const rate = d.total_students > 0
          ? (((d.present_count + d.late_count) / d.total_students) * 100).toFixed(0)
          : '0';
        return (
          <div key={d.id} className="att-history-row">
            <div className="att-history-date">
              <MdEventAvailable size={18} />
              <div>
                <b>{d.date}</b>
                <TakenInfo day={d} />
              </div>
            </div>
            <div className="att-history-counts">
              <span className="att-pill is-present"><MdCheckCircle size={14} /> {d.present_count}</span>
              <span className="att-pill is-absent"><MdCancel size={14} /> {d.absent_count}</span>
              <span className="att-pill is-late"><MdAccessTime size={14} /> {d.late_count}</span>
            </div>
            <div className="att-history-rate">
              <div className="att-mini-bar"><div className="att-mini-fill" style={{ width: `${rate}%` }} /></div>
              <span>{rate}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Month grid: students as rows, dates as columns. */
function GridView({ grid }) {
  if (!grid) return null;
  const cellIcon = (c) => {
    if (!c) return <span className="att-cell-empty">–</span>;
    if (c.status === 'present') return <MdCheckCircle className="att-cell-icon is-present" size={17} />;
    if (c.status === 'late') return <MdAccessTime className="att-cell-icon is-late" size={17} />;
    return <MdCancel className="att-cell-icon is-absent" size={17} />;
  };
  return (
    <div className="att-gridview-scroll">
      <table className="att-gridview">
        <thead>
          <tr>
            <th className="att-gridview-name">اسم الطالب</th>
            {grid.dates.map((d) => <th key={d} className="att-gridview-date">{dayMonth(d)}</th>)}
            <th className="att-gridview-sum">حضور</th>
            <th className="att-gridview-sum">غياب</th>
            <th className="att-gridview-sum">النسبة</th>
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((r) => (
            <tr key={r.id}>
              <td className="att-gridview-name">{r.name}</td>
              {r.perDay.map((c, i) => (
                <td key={i} className="att-gridview-cell" title={c?.note || undefined}>{cellIcon(c)}</td>
              ))}
              <td className="att-gridview-sum is-present">{r.attended}</td>
              <td className="att-gridview-sum is-absent">{r.absentDays}</td>
              <td className="att-gridview-sum"><b>{r.rate}%</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Every correction made to this section's registers in the range. */
function ChangesView({ sectionId, startDate, endDate, reloadKey }) {
  const [rows, setRows] = useState(null);
  const [lateOnly, setLateOnly] = useState(false);
  useEffect(() => {
    let alive = true;
    setRows(null);
    api.sectionAttendanceChanges(sectionId, { start_date: startDate, end_date: endDate, late_only: lateOnly ? 1 : undefined })
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [sectionId, startDate, endDate, lateOnly, reloadKey]);

  if (!rows) return <div className="att-loading">جارٍ التحميل...</div>;
  return (
    <>
      <label className="att-changes-filter">
        <input type="checkbox" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} /> التعديلات اللاحقة فقط
      </label>
      <Table
        keyField="id"
        rows={rows}
        emptyText="لا توجد تعديلات في هذه الفترة"
        searchPlaceholder="ابحث بالاسم أو الملاحظة..."
        pageSize={25}
        columns={[
          { key: 'date', label: 'يوم التفقّد', nowrap: true, width: 1,
            render: (r) => <span className="att-date-chip">{r.date}</span> },
          { key: 'student_name', label: 'الطالب', width: 2, filterable: false,
            render: (r) => <strong>{r.student_name}</strong> },
          { key: 'old_status', label: 'التعديل', nowrap: true, width: 1, filterable: false,
            render: (r) => (
              <span className="att-change-arrow">
                <span className={`att-status-badge is-${r.old_status}`}>{STATUS_AR[r.old_status]}</span>
                ←
                <span className={`att-status-badge is-${r.new_status}`}>{STATUS_AR[r.new_status]}</span>
              </span>
            ) },
          { key: 'changed_by_name', label: 'بواسطة', nowrap: true, width: 1, formatValue: (v) => v || '—' },
          { key: 'changed_at', label: 'وقت التعديل', nowrap: true, width: 1, filterable: false,
            render: (r) => (
              <span className="att-status-cell">
                <bdi dir="ltr">{fmtWhen(r.changed_at)}</bdi>
                {r.changed_late ? <span className="att-flag is-danger">يوم لاحق</span> : null}
              </span>
            ) },
          { key: 'note', label: 'السبب', width: 2, filterable: false, clamp: 2, formatValue: (v) => v || '—' },
        ]}
      />
    </>
  );
}
